import { useState, useEffect, useRef, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';

/**
 * pre169: kleines Menü neben dem Übersetzungs-Button (analog Mic/Kamera/Lautsprecher).
 * Es übernimmt die gesprächsspezifischen Übersetzungs-Steuerungen, die vorher in
 * der rechten Leiste standen:
 *   - Übersetzungssprache an der Stele (Kundenseite)
 *   - Sprachauswahl beim Kunden öffnen
 *   - Nachricht an den Kunden (auto-übersetzt) senden
 * Die Berater-Sprache und die Untertitel-Größen liegen jetzt in den Einstellungen
 * (sie ändern sich selten bzw. gelten dauerhaft).
 *
 * Öffnet nach OBEN (die Toolbar sitzt am unteren Bühnenrand) und wird per Portal
 * an document.body gerendert, damit kein Stacking-Context der Toolbar geerbt wird.
 */
export function TranslationMenu() {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; bottom: number } | null>(null);
  const [msg, setMsg] = useState('');
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setPos({ left: r.left + r.width / 2, bottom: window.innerHeight - r.top + 6 });
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const lbl: CSSProperties = { fontSize: 10, opacity: 0.5, textTransform: 'uppercase', letterSpacing: 0.5, margin: '8px 2px 4px' };
  const surface: CSSProperties = { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: '#f5f5f0', borderRadius: 6, fontFamily: 'inherit' };
  const pick: CSSProperties = { ...surface, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 10px', fontSize: 12, fontWeight: 500, cursor: 'pointer' };
  const action: CSSProperties = { ...surface, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: 8, fontSize: 11, fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase', cursor: 'pointer', marginTop: 4 };

  return (
    <>
      <button
        ref={btnRef}
        title="Übersetzungs-Optionen"
        onClick={() => setOpen((o) => !o)}
        style={{
          width: 16, height: 28, border: 'none', background: 'transparent', cursor: 'pointer',
          opacity: 0.9, padding: 0, marginLeft: -5,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && pos && createPortal(
        <div
          ref={menuRef}
          style={{
            position: 'fixed', left: pos.left, bottom: pos.bottom, transform: 'translateX(-50%)',
            width: 268, background: '#1c1c1a', color: '#f5f5f0',
            border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,0.5)', zIndex: 1000, textAlign: 'left',
          }}
        >
          <div style={{ ...lbl, marginTop: 0 }}>Sprache an der Stele</div>
          <button style={pick}>
            <span>🇹🇷 Türkçe</span>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          <button style={action}>
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="2" y1="12" x2="22" y2="12" />
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
            </svg>
            Sprachauswahl beim Kunden öffnen
          </button>

          <div style={lbl}>Nachricht an Kunde (übersetzt)</div>
          <textarea
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
            placeholder="Nachricht an Kunde (auto-übersetzt)…"
            style={{ ...surface, width: '100%', padding: '7px 9px', fontSize: 12, outline: 'none', resize: 'none', minHeight: 52 }}
          />
          <button style={{ ...action, background: '#E2001A', borderColor: '#E2001A', color: '#fff' }}>Senden → 🇹🇷</button>
        </div>,
        document.body,
      )}
    </>
  );
}
